// @ts-check
import invariant from 'invariant';
import { ReferralType } from './types'; // eslint-disable-line no-unused-vars

/** @type {{[x: string]: ReferralType}} */
const referralTypes = {
  BOOKED_REFERRAL: 'BOOKED_REFERRAL',
  UNBOOKED_REFERRAL: 'UNBOOKED_REFERRAL',
  ORGANIZATION_REFERRAL: 'ORGANIZATION_REFERRAL',
  UNASSIGNED_REFERRAL: 'UNASSIGNED_REFERRAL',
  UNCLAIMED_REFERRAL: 'UNCLAIMED_REFERRAL',
};

/**
 * @param {{appointmentSlotId?: string, providerMappingId?: string, locationId?: string, organizationId?: string}} urlQuery
 * @returns {ReferralType}
 */
export function referralTypeForQueryParams(urlQuery) {
  const { appointmentSlotId, providerMappingId, locationId, organizationId } = urlQuery;
  if (!!appointmentSlotId) {
    return referralTypes.BOOKED_REFERRAL;
  } else if (!!providerMappingId) {
    return referralTypes.UNBOOKED_REFERRAL;
  } else if (!!locationId) {
    return referralTypes.UNASSIGNED_REFERRAL;
  } else if (!!organizationId) {
    return referralTypes.ORGANIZATION_REFERRAL;
  } else {
    // NB: Unclaimed referrals are not mentioned here because they do not use the step 4 confirmation page; instead they
    // use a modal in step 3 presently.
    throw new Error('Invalid query params'); // Should be unreachable due to ensureLocationInvariant.
  }
}

/** @typedef {(apiBase: string, referralProfileId: string) => string} urlBuilderType */
/** @type {{forPost: {[x: string]: urlBuilderType}}} */
export const urlBuilder = {
  forPost: {
    BOOKED_REFERRAL: (apiBase, referralProfileId) =>
      `${apiBase}/referral_profiles/${referralProfileId}/booked_referrals`,
    UNBOOKED_REFERRAL: (apiBase, referralProfileId) =>
      `${apiBase}/referral_profiles/${referralProfileId}/unbooked_referrals`,
    UNASSIGNED_REFERRAL: (apiBase, referralProfileId) =>
      `${apiBase}/referral_profiles/${referralProfileId}/unassigned_referrals`,
    ORGANIZATION_REFERRAL: (apiBase, referralProfileId) =>
      `${apiBase}/referral_profiles/${referralProfileId}/organization_referrals`,
  },
};

/**
 * Helper to tell whether required query params were provided in page URL
 *
 * @param {{appointmentSlotId?: string, providerMappingId?: string, locationId?: string, organizationId?: string}} queryParams
 * @returns {boolean}
 */
function ensureRequiredQueryParamsPresent(queryParams) {
  const { appointmentSlotId, providerMappingId, locationId, organizationId } = queryParams;
  return !!(
    (
      (appointmentSlotId && providerMappingId) || // for booked referral
      providerMappingId || // for unbooked referral
      locationId || // for unassigned referral
      organizationId
    ) // for organization_referral
  );
}

function queryParamsMissingMessage(queryParams) {
  return `
    \`location.query\` is missing required params to fetch ReferralConfirmation page data.
    One of the following sets of values is required (? denotes optional):
      For Booked Referral: \`appointmentSlotId\` && \`providerMappingId\` && \`specialtyId?\`,
      For Unbooked Referral: \`providerMappingId\` && \`specialtyId?\`,
      For Unassigned Referral: \`locationId\` && \`specialtyId?\`,
      For Organization Referral: \`organizationId\` && \`specialtyId?\`
    What you provided was:
      ${JSON.stringify(queryParams)}
  `;
}

export function ensureLocationInvariant(query) {
  invariant(ensureRequiredQueryParamsPresent(query), queryParamsMissingMessage(query));
}
